/**
 * "ダッシュボード" — project status overview centered on the current
 * phase's migration-task progress.
 *
 * Surfaces (see `lib/dashboard.ts` for the exact aggregation contracts):
 *   A. Current phase total / done / in-progress / blocked + days remaining
 *   B. Today: started / completed / actively in-progress now
 *   C. Overdue task list (count + worst offenders sorted)
 *   D. Assignee workload chart
 *   E. Area progress chart
 *   F. 7-day activity sparkline
 *   G. Warnings (cycle deps + unassigned count)
 *
 * Layout: multi-column on md+ (KPI grid 4-up, two-column charts), single
 * column on mobile with horizontally-scrollable KPI strip.
 */
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { normalizeCatalogOptions } from '@mct/shared';
import {
  useActivityLogs,
  useAssignees,
  useExecutionsForRun,
  useProject,
  useRuns,
  useTasks,
} from '../hooks/useFirestoreData';
import { Spinner } from '../components/ui/Spinner';
import { KpiCard } from '../components/Dashboard/KpiCard';
import { PhaseProgressBar } from '../components/Dashboard/PhaseProgressBar';
import { OverdueTaskList } from '../components/Dashboard/OverdueTaskList';
import { AssigneeWorkloadChart } from '../components/Dashboard/AssigneeWorkloadChart';
import { AreaProgressChart } from '../components/Dashboard/AreaProgressChart';
import { ActivitySparkline } from '../components/Dashboard/ActivitySparkline';
import { ThisWeekDueByPhase } from '../components/Dashboard/ThisWeekDueByPhase';
import { useWbsItems } from '../hooks/useWbs';
import {
  computeAreaProgress,
  computeAssigneeWorkload,
  computeCurrentPhaseProgress,
  computeOverdueTasks,
  computeRecentActivity,
  computeThisWeekDueByPhase,
  computeTodaySituation,
  computeWarnings,
  pickCurrentRun,
} from '../lib/dashboard';

export function DashboardPage() {
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId) return null;
  return <DashboardContent projectId={projectId} />;
}

function DashboardContent({ projectId }: { projectId: string }): JSX.Element {
  // Pin `now` once per render so date math is stable across all
  // aggregators in the same paint. We don't tick — the page refreshes on
  // user navigation, which is the right granularity here.
  const now = useMemo(() => new Date(), []);
  const { data: project, loading: projectLoading } = useProject(projectId);
  const { data: tasks } = useTasks(projectId);
  const { data: runs } = useRuns(projectId);
  const { data: assignees } = useAssignees(projectId);
  const { data: logs } = useActivityLogs(projectId, 200);
  const { data: wbsItems } = useWbsItems(projectId);

  const currentRun = useMemo(() => pickCurrentRun(runs), [runs]);
  const { data: executions } = useExecutionsForRun(projectId, currentRun?.runId ?? null);

  const areaCatalog = useMemo(
    () => normalizeCatalogOptions(project?.wbsCatalog?.area ?? []),
    [project?.wbsCatalog?.area],
  );

  const phase = useMemo(
    () => computeCurrentPhaseProgress(tasks, executions, currentRun, now),
    [tasks, executions, currentRun, now],
  );
  const today = useMemo(
    () => computeTodaySituation(executions, currentRun, now),
    [executions, currentRun, now],
  );
  const overdue = useMemo(
    () => computeOverdueTasks(tasks, executions, currentRun, now),
    [tasks, executions, currentRun, now],
  );
  const workload = useMemo(
    () => computeAssigneeWorkload(tasks, executions, currentRun, assignees),
    [tasks, executions, currentRun, assignees],
  );
  const areaRows = useMemo(
    () => computeAreaProgress(tasks, executions, currentRun, areaCatalog),
    [tasks, executions, currentRun, areaCatalog],
  );
  const activity = useMemo(
    () => computeRecentActivity(logs, now, 7),
    [logs, now],
  );
  const warnings = useMemo(() => computeWarnings(tasks), [tasks]);
  const thisWeekDuePhases = useMemo(
    () => computeThisWeekDueByPhase(wbsItems, now),
    [wbsItems, now],
  );
  const thisWeekDueCount = useMemo(() => {
    let n = 0;
    for (const p of thisWeekDuePhases) {
      for (const proj of p.projectGroups) {
        for (const a of proj.assigneeGroups) n += a.rows.length;
      }
    }
    return n;
  }, [thisWeekDuePhases]);

  if (projectLoading || !project) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const phaseDateLabel = phase.plannedStart && phase.plannedEnd
    ? `${format(phase.plannedStart, 'M/d')} – ${format(phase.plannedEnd, 'M/d')}`
    : '日程未設定';
  // `pickCurrentRun` falls back to the lowest-order run when nothing is
  // active, so a non-null `currentRun` does NOT imply "in progress". We
  // surface that distinction here so the header label, banner, and KPI
  // strip stop suggesting work is underway when it isn't.
  const hasActiveRun = currentRun?.status === 'active';
  const phaseLabel = hasActiveRun ? '現在のフェーズ' : '次のフェーズ';

  return (
    <div
      data-testid="dashboard-page-root"
      className="h-full overflow-auto px-3 py-4 sm:px-6 sm:py-6"
    >
      <header className="mb-4 flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-slate-900">ダッシュボード</h2>
        {currentRun ? (
          <p className="text-sm text-slate-600">
            {phaseLabel}:
            <span className="ml-1 font-medium text-slate-800">{currentRun.name}</span>
            <span className="ml-2 text-slate-500">
              ({phaseDateLabel}
              {hasActiveRun && phase.remainingDays != null ? `、残り ${phase.remainingDays} 日` : ''})
            </span>
          </p>
        ) : (
          <p className="text-sm text-slate-500">フェーズが登録されていません</p>
        )}
      </header>

      {/* This-week-due WBS tasks — top of page by product intent so the
          PM sees "what's due this week and not done yet" before any
          phase KPIs. Grouped phase → Backlog project → assignee. */}
      <section
        data-testid="dashboard-this-week-due"
        className="mb-6"
      >
        <div className="mb-2 flex items-baseline justify-between">
          <h3 className="text-sm font-semibold text-slate-800">今週期限のタスク</h3>
          <span className="text-xs text-slate-500 tabular-nums">{thisWeekDueCount} 件</span>
        </div>
        <ThisWeekDueByPhase
          phases={thisWeekDuePhases}
          assignees={assignees}
          spaceUrl={project.backlog?.spaceUrl ?? null}
        />
      </section>

      {currentRun && !hasActiveRun ? (
        <div
          data-testid="dashboard-no-active-banner"
          className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
        >
          進行中のフェーズはありません。
        </div>
      ) : null}

      {/* KPI strip — 2-col on mobile, 4-col on md+. Reads as a single
          horizontal row that fits inside the layout's overflow-auto.
          Values blank to "—" while no run is active so the strip stops
          implying work is in progress; the detail sections below stay
          populated as a preview of the upcoming phase. */}
      <section
        data-testid="dashboard-kpis"
        className="mb-4 grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-4"
      >
        <KpiCard
          label="完了"
          value={hasActiveRun ? phase.done : '—'}
          sublabel={hasActiveRun ? `/${phase.effectiveTotal}` : undefined}
          hint={hasActiveRun ? `${phase.donePercent}%` : undefined}
          tone={hasActiveRun ? 'done' : 'neutral'}
          testId="kpi-done"
        />
        <KpiCard
          label="進行中"
          value={hasActiveRun ? phase.inProgress : '—'}
          tone={hasActiveRun ? 'progress' : 'neutral'}
          testId="kpi-in-progress"
        />
        <KpiCard
          label="遅延"
          value={hasActiveRun ? overdue.length : '—'}
          hint={
            hasActiveRun
              ? phase.plannedEnd && phase.plannedEnd.getTime() < now.getTime()
                ? 'フェーズ期日超過'
                : '期日内'
              : undefined
          }
          tone={hasActiveRun && overdue.length > 0 ? 'blocked' : 'neutral'}
          testId="kpi-overdue"
        />
        <KpiCard
          label="ブロック"
          value={hasActiveRun ? phase.blocked : '—'}
          tone={hasActiveRun && phase.blocked > 0 ? 'blocked' : 'neutral'}
          testId="kpi-blocked"
        />
      </section>

      {/* Phase progress bar */}
      <section className="mb-6 rounded-lg border border-slate-200 bg-white p-3 sm:p-4">
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-800">フェーズ進捗</h3>
          <span className="text-xs text-slate-500 tabular-nums">
            {phase.done}/{phase.effectiveTotal} ({phase.donePercent}%)
          </span>
        </div>
        <PhaseProgressBar breakdown={phase} />
      </section>

      {/* Today / activity row. Two columns on md+. */}
      <section className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4">
          <h3 className="mb-2 text-sm font-semibold text-slate-800">本日の状況</h3>
          <dl className="grid grid-cols-3 gap-2 text-center">
            <TodayCell label="開始" value={today.startedToday} />
            <TodayCell label="完了" value={today.completedToday} />
            <TodayCell label="進行中" value={today.inProgressNow} />
          </dl>
          <p className="mt-2 text-[11px] text-slate-500">
            実行レコードの開始日時 / 完了日時から算出。
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4">
          <h3 className="mb-2 text-sm font-semibold text-slate-800">直近 7 日のアクティビティ</h3>
          {/* Horizontally scrollable on narrow screens so the sparkline
              keeps its native aspect ratio rather than squishing. */}
          <div className="overflow-x-auto">
            <ActivitySparkline buckets={activity} />
          </div>
        </div>
      </section>

      {/* Area + assignee charts. Two columns on md+. */}
      <section className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4">
          <h3 className="mb-2 text-sm font-semibold text-slate-800">領域別進捗</h3>
          <div className="overflow-x-auto">
            <AreaProgressChart rows={areaRows} />
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4">
          <h3 className="mb-2 text-sm font-semibold text-slate-800">担当者別ワークロード</h3>
          <div className="overflow-x-auto">
            <AssigneeWorkloadChart rows={workload} />
          </div>
        </div>
      </section>

      {/* Overdue task list */}
      <section className="mb-6">
        <div className="mb-2 flex items-baseline justify-between">
          <h3 className="text-sm font-semibold text-slate-800">遅延タスク</h3>
          <span className="text-xs text-slate-500 tabular-nums">{overdue.length} 件</span>
        </div>
        <OverdueTaskList rows={overdue} assignees={assignees} projectId={projectId} />
      </section>

      {/* Warnings strip — surfaces low-rate, high-impact issues so the
          PM notices structural problems without having to hunt them. */}
      {warnings.cycleTaskCount > 0 || warnings.unassignedCount > 0 ? (
        <section className="mb-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {warnings.cycleTaskCount > 0 ? (
            <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-800">
              <span className="font-semibold">循環依存:</span> {warnings.cycleTaskCount} 件のタスクで検出
            </div>
          ) : null}
          {warnings.unassignedCount > 0 ? (
            <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-800">
              <span className="font-semibold">未割当:</span> {warnings.unassignedCount} 件
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

function TodayCell({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="rounded border border-slate-100 bg-slate-50 px-2 py-2">
      <dt className="text-[11px] text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-xl font-semibold tabular-nums text-slate-900">{value}</dd>
    </div>
  );
}
